// src/components/DocumentPreviewModal.js
import React, { useEffect, useCallback, useState } from "react";
import { createPortal } from "react-dom";

export default function DocumentPreviewModal({
  open,
  onClose,
  documents = [],
  startIndex = 0,
}) {
  const [index, setIndex] = useState(startIndex);
  const [animDir, setAnimDir] = useState(null);

  const maxIndex = documents.length - 1;
  const doc = documents[index];

  // 🔑 USE PRESIGNED URL
  const fileUrl = doc?.preview_url || null;

  const title =
    doc?.document_type_name
      ? `${doc.document_type_name} — ${doc.file_name}`
      : doc?.file_name || "Document";

  const isImage = (url) =>
    /\.(png|jpe?g|webp|gif|bmp|tiff)(\?.*)?$/i.test(url || "");

  const isPDF = (url) =>
    /\.(pdf)(\?.*)?$/i.test(url || "");

  // ----------------------------------------
  // ESC to close
  // ----------------------------------------
  const onEsc = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;

    document.addEventListener("keydown", onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prev;
    };
  }, [open, onEsc]);

  useEffect(() => {
    if (open) setIndex(startIndex);
  }, [open, startIndex]);

  if (!open) return null;

  // ----------------------------------------
  // Navigation
  // ----------------------------------------
  const goPrev = () => {
    if (index > 0) {
      setAnimDir("left");
      setTimeout(() => {
        setIndex((i) => i - 1);
        setAnimDir(null);
      }, 150);
    }
  };

  const goNext = () => {
    if (index < maxIndex) {
      setAnimDir("right");
      setTimeout(() => {
        setIndex((i) => i + 1);
        setAnimDir(null);
      }, 150);
    }
  };

  // ----------------------------------------
  // Renderer
  // ----------------------------------------
  const renderContent = () => {
    if (!fileUrl) {
      return (
        <div className="p-8 text-center text-red-600">
          Preview link expired or unavailable.
        </div>
      );
    }

    if (isImage(fileUrl)) {
      return (
        <img
          src={fileUrl}
          alt={title}
          className="max-h-[80vh] w-auto object-contain rounded-lg shadow"
        />
      );
    }

    if (isPDF(fileUrl)) {
      return (
        <iframe
          title={title}
          src={fileUrl}
          className="w-[85vw] max-w-5xl h-[80vh] rounded-lg border bg-white"
        />
      );
    }

    return (
      <div className="p-6 text-center">
        <p className="text-sm text-gray-600">Preview not available.</p>
        <a
          href={fileUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex mt-4 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
        >
          Open / Download
        </a>
      </div>
    );
  };

  const slideClass =
    animDir === "right"
      ? "animate-slide-left"
      : animDir === "left"
      ? "animate-slide-right"
      : "";

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50"></div>

      <div
        className="relative z-[2100] bg-white rounded-2xl shadow-2xl border max-w-[92vw]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-base font-semibold truncate max-w-[70vw]">
            {title}
          </h3>

          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-gray-100"
          >
            ✕
          </button>
        </div>

        {/* CONTENT */}
        <div className="relative px-16 py-6 flex items-center justify-center">
          <button
            onClick={goPrev}
            disabled={index === 0}
            className={`absolute left-0 top-1/2 -translate-y-1/2 p-3
              ${index === 0 ? "opacity-30" : "hover:scale-125"}`}
          >
            ‹
          </button>

          <div className={`flex justify-center w-full ${slideClass}`}>
            {renderContent()}
          </div>

          <button
            onClick={goNext}
            disabled={index === maxIndex}
            className={`absolute right-0 top-1/2 -translate-y-1/2 p-3
              ${index === maxIndex ? "opacity-30" : "hover:scale-125"}`}
          >
            ›
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideLeft {
          from { opacity: 0; transform: translateX(60px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideRight {
          from { opacity: 0; transform: translateX(-60px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slide-left { animation: slideLeft 0.18s ease-out; }
        .animate-slide-right { animation: slideRight 0.18s ease-out; }
      `}</style>
    </div>,
    document.body
  );
}